import { useState } from "react"

import { Datasets } from "../../../../../constants/enums"
import { SUPPORT_REFER } from "../../../../../errors/errorMessages"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { snackbar } from "../../../../../utils/general/generalUtils"
import { postClientProtected } from "../../../clients/clients"
import {
    PUBLISH_LCT_ATTACHMENT,
    PUBLISH_TTR_ATTACHMENT,
    UNPUBLISH_LCT_ATTACHMENT,
    UNPUBLISH_TTR_ATTACHMENT,
} from "../../../urls/private"

interface IUseSetUploadedAttachmentRestrictedOutput {
    setUploadedAttachmentRestricted: (
        restricted: boolean,
        fileName: string,
        deviceRef?: string,
        attachmentUUID?: string
    ) => Promise<void>
    isLoading: boolean
    isLoaded: boolean
    error?: AppError
}

/** Hook provides a function for setting the restricted state of an uploaded device attachment */
const useSetUploadedAttachmentRestricted = (
    isLct: boolean,
    dataset: Datasets,
    isAmendment?: boolean
): IUseSetUploadedAttachmentRestrictedOutput => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<AppError | undefined>(undefined)
    const [isLoaded, setIsLoaded] = useState<boolean>(false)

    const setUploadedAttachmentRestricted = async (
        restricted: boolean,
        fileName: string,
        deviceRef?: string,
        attachmentUUID?: string
    ): Promise<void> => {
        let url
        let body
        if (isLct) {
            url = restricted
                ? UNPUBLISH_LCT_ATTACHMENT(dataset, isAmendment)
                : PUBLISH_LCT_ATTACHMENT(dataset, isAmendment)
            body = {
                fileName,
                attachment_uuid: attachmentUUID,
            }
        } else {
            if (!deviceRef) {
                return
            }
            url = restricted ? UNPUBLISH_TTR_ATTACHMENT : PUBLISH_TTR_ATTACHMENT
            body = {
                deviceRef,
                fileName,
                attachment_uuid: attachmentUUID,
            }
        }

        setError(undefined)
        setIsLoading(false)
        setIsLoaded(false)

        try {
            setIsLoading(true)
            await postClientProtected(url, body)
            snackbar("Successfully edited attachment", "success")
            setIsLoaded(true)
        } catch {
            const appError = invalidApiDataError("Couldn't edit attachment")
            setError(appError)
            snackbar(
                `Couldn't edit attachment, please try again. ${SUPPORT_REFER}`,
                "warning"
            )
        } finally {
            setIsLoading(false)
        }
    }

    return {
        setUploadedAttachmentRestricted,
        isLoaded,
        isLoading,
        error,
    }
}

export default useSetUploadedAttachmentRestricted
